import { useCallback, useState } from "react";
import { BiEditAlt, BiTrash, BiReply } from "react-icons/bi";
import { CommentIcon, ReplyArrowIcon } from "../../../utils/customIcons";
import { usePermissions } from "../../../app/hooks";
import KebabMenu, { Line } from "../../kebabMenu/KebabMenu";
import CommentTextEditorContainer from "./CommentTextEditorContainer";
import ReactQuill from "react-quill-new";
import ConfirmationModal from "../../modal/ConfirmationModal";
import { TranslationKey } from "../../../types/modulesInterfaces";
import TranslatableText from "../../translations/TranslatableText";

export interface Comment {
	id: number;
	author: User;
	content: string;
	replies?: Reply[];
	parent: number;
}
interface User {
	id: number;
	email: string;
}
interface Reply {
	author: User;
	content: string;
	replies: Reply[];
	id: number;
}
interface ContentBlockProps {
	comment: Comment;
	deleteHandler: (id: number) => void;
	updateHandler: (id: number, content: string) => void;
	createHandler: ({
		content,
		parent,
	}: { content: string; parent: number }) => void;
	id: string;
}

const CommentBlock: React.FC<ContentBlockProps> = ({
	comment,
	deleteHandler,
	updateHandler,
	createHandler,
	id
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isReplyActive, setIsReplyActive] = useState<boolean>(false);
	const [isEditActive, setIsEditActive] = useState<boolean>(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
	const [commentToDelete, setCommentToDelete] = useState<number | null>(null);
	const { canMutateComment } = usePermissions();

	const handleCreateReply = useCallback(
		({ content }: { content: string }) => {
			createHandler({ content, parent: comment.id });
			setIsReplyActive(!isReplyActive);
		},
		[createHandler, comment.id, isReplyActive],
	);

	const handleUpdateComment = useCallback(
		({ content }: { content: string }) => {
			updateHandler(comment.id, content);
			setIsEditActive(false);
		},
		[updateHandler, comment.id],
	);

	const handleConfirmDelete = useCallback(() => {
		if (commentToDelete !== null) {
			deleteHandler(commentToDelete);
			setShowDeleteConfirm(false);
			setCommentToDelete(null);
		}
	}, [deleteHandler, commentToDelete]);

	const handleCancelDelete = useCallback(() => {
		setShowDeleteConfirm(false);
		setCommentToDelete(null);
	}, []);

	const openDeleteConfirm = useCallback((id: number) => {
		setCommentToDelete(id);
		setShowDeleteConfirm(true);
	}, []);

	let lines: Line[] = [
		{
			id: 0,
			label: "main.reply",
			icon: BiReply,
			clickHandler: () => setIsReplyActive(!isReplyActive),
		},
	];

	if (canMutateComment(comment.author.id)) {
		lines.push(
			{
				id: 1,
				label: "main.edit",
				icon: BiEditAlt,
				clickHandler: () => setIsEditActive(true),
			},
			{
				id: 2,
				label: "main.delete",
				icon: BiTrash,
				clickHandler: () => openDeleteConfirm(comment.id),
			}
		);
	}

	return (
		<article
			className="comment-block"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			id={id}
		>
			<div className="d-flex align-items-start justify-content-between">
				<div
					className="d-flex align-items-start"
					style={{ paddingLeft: "3px" }}
				>
					<CommentIcon
						fill={isHovered ? "yes" : ""}
						style={{ paddingTop: "0.2rem" }}
					/>
					<div className="d-flex f-column align-items-start fs-13 ps-2">
						<div className="d-flex f-column align-items-start text-darker-gray">
							<strong className="ff-bold">Comment #{comment.id}</strong>
							<span>by: <strong className="ff-bold">{comment.author.email}</strong></span>
						</div>

						{!isEditActive && <ReactQuill
							modules={{ toolbar: false }}
							theme="snow"
							value={comment.content}
							readOnly={true}
						/>}
					</div>
				</div>

				{!isEditActive && (
					<KebabMenu
						lines={lines}
						activeColor="var(--tertiary-deep)"
						buttonClasses={isHovered ? "" : "hidden"}
						position="left"
					/>
				)}
			</div>

			{comment.replies
				? comment.replies.map((reply, index) => {
					let replyLines: Line[] = []
					if (canMutateComment(reply.author.id)) {
						replyLines.push({
							id: 0,
							label: "main.delete",
							icon: BiTrash,
							clickHandler: () => openDeleteConfirm(reply.id),
						})
					}
					return (
						<div className="d-flex align-items-start ms-2 pt-1" style={{ borderTop: '1px solid var(--gray)' }} key={`${reply.author}-${index}`}>
							<ReplyArrowIcon />
							<div className="d-flex f-column justify-content-start pt-1">
								<span className="text-darker-gray">
									Reply: <strong className="ff-bold fs-13">{reply.author.email}</strong>
								</span>
								<ReactQuill
									modules={{ toolbar: false }}
									theme="snow"
									value={reply.content}
									readOnly={true}
								/>
							</div>
							<KebabMenu
								lines={replyLines}
								activeColor="var(--tertiary-deep)"
								buttonClasses={isHovered ? "" : "hidden"}
								position="left"
							/>
						</div>
					);
				})
				: null}
			<CommentTextEditorContainer
				isCommentActive={isReplyActive}
				cancelCallback={() => setIsReplyActive(!isReplyActive)}
				okCallback={handleCreateReply}
			/>
			{isEditActive ? (
				<CommentTextEditorContainer
					isCommentActive={true}
					cancelCallback={() => setIsEditActive(false)}
					okCallback={handleUpdateComment}
					initialContent={comment.content}
				/>
			) : null}

			<ConfirmationModal
				open={showDeleteConfirm}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
				message={<TranslatableText translationKey="main.delete_comment_confirmation" />}
				headerText="main.delete"
			/>
		</article>
	);
};
export default CommentBlock;
